import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { placesAutocomplete, placeDetails } from "../lib/api";
import { Input } from "./ui/input";

const token = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

// Address field with Google lookup as you type. Falls back to a plain input when lookup isn't configured.
// onSelect receives {formatted, name, line1, line2, city, state, zip, country, lat, lng, phone, website}.
export const AddressInput = ({ value, onChange, onSelect, mode = "address", placeholder, as: Comp = Input, testId = "address-input", className = "", wrapClass = "", ...rest }) => {
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [hi, setHi] = useState(-1);
  const session = useRef(token());
  const timer = useRef(null);
  const skip = useRef(false);
  const box = useRef(null);
  const typed = useRef(false);
  useEffect(() => {
    // Only look up after the user types — not on mount, StrictMode re-runs, or values loaded from the server.
    if (!typed.current) return;
    if (skip.current) { skip.current = false; return; }
    const q = (value || "").trim();
    clearTimeout(timer.current);
    if (disabled || q.length < 3) { setSugs([]); return; }
    timer.current = setTimeout(async () => {
      try { setBusy(true); const r = await placesAutocomplete(q, session.current, mode); setSugs(r.suggestions || []); setOpen(true); setHi(-1); }
      catch (e) { if (e?.response?.status === 424) setDisabled(true); setSugs([]); }
      finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [value, mode, disabled]);
  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);
  const pick = async (s) => {
    setOpen(false); setSugs([]); skip.current = true;
    try {
      const d = await placeDetails(s.place_id, session.current);
      session.current = token();
      const label = mode === "place" && d.name ? `${d.name}, ${d.formatted}` : d.formatted || s.text;
      onChange(label);
      onSelect?.(d);
    } catch { onChange(s.text); }
  };
  const onKey = (e) => {
    if (!open || !sugs.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, sugs.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && hi >= 0) { e.preventDefault(); pick(sugs[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };
  return (
    <div ref={box} className={`relative ${wrapClass}`} data-testid={`${testId}-wrap`}>
      <Comp value={value || ""} onChange={(e) => { typed.current = true; onChange(e.target.value); }} onKeyDown={onKey} onFocus={() => sugs.length && setOpen(true)} placeholder={placeholder} autoComplete="off" data-testid={testId} className={className} {...rest} />
      {busy && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />}
      {open && sugs.length > 0 && (
        <ul className="absolute left-0 z-50 mt-1 w-full min-w-[20rem] max-w-[90vw] overflow-hidden rounded-lg border border-border bg-popover shadow-lg text-sm" role="listbox" data-testid={`${testId}-suggestions`}>
          {sugs.map((s, i) => (
            <li key={s.place_id} role="option" aria-selected={i === hi} onMouseDown={(e) => { e.preventDefault(); pick(s); }} onMouseEnter={() => setHi(i)}
                className={`flex items-start gap-2 px-3 py-2 cursor-pointer ${i === hi ? "bg-muted" : ""}`} data-testid={`${testId}-suggestion-${i}`}>
              <MapPin className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
              <span className="min-w-0"><span className="block truncate">{s.main || s.text}</span>{s.secondary && <span className="block truncate text-xs text-muted-foreground">{s.secondary}</span>}</span>
            </li>
          ))}
          <li className="px-3 py-1 text-[10px] text-muted-foreground text-right">Powered by Google</li>
        </ul>
      )}
    </div>
  );
};
